import { useEffect, useState } from 'react';
import './daily-games.styles.scss';
import { MAX_TIMES, validateAutomation } from '@shared/automation.js';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { midSentence, nextGamesText, whenText } from '@/utils/nextGames';
import { inOffseason } from '@/utils/leagueLabels';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

// Time zones offered; the league's current one and the admin's own are
// added if missing.
const ZONES = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix',
    'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'Etc/UTC',
];

// An admin turns a league's daily games on or off and sets when they play.
const DailyGames = ({ league }) => {

    const auto = league.automation ?? { enabled: false, times: ['20:00'], timeZone: 'America/Chicago' };
    const [ form, setForm ] = useState(null);
    const [ saving, setSaving ] = useState(false);
    const [ message, setMessage ] = useState(null);
    const ownZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const zones = [...new Set([...ZONES, auto.timeZone, ownZone].filter(Boolean))];

    // Start editing from what's saved (and again whenever it changes).
    const savedKey = JSON.stringify([auto.enabled, auto.times, auto.timeZone]);
    useEffect(() => {
        setForm({ enabled: auto.enabled, times: [...auto.times], timeZone: auto.timeZone });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedKey])

    if (!form) return null;
    const errors = validateAutomation(form);
    const dirty = JSON.stringify([form.enabled, [...form.times].sort(), form.timeZone]) !==
        JSON.stringify([auto.enabled, [...auto.times].sort(), auto.timeZone]);
    const next = nextGamesText(league);
    const offseason = inOffseason(league.season);
    const perDay = league.settings.gameDaysPerRealDay;

    async function save(changes = {}) {
        const body = { ...form, ...changes };
        setSaving(true);
        setMessage(null);
        const res = await UseCloudFunction('updateAutomation', { leagueId: league.id, ...body });
        setSaving(false);
        setMessage(res.error ? { error: true, text: res.error } : { text: body.enabled ? 'Saved. Games will play at these times.' : 'Saved. Daily games are off.' });
    }

    const setTime = (i, value) => setForm({ ...form, times: form.times.map((t, j) => (j === i ? value : t)) });

    return (
        <div className='g-card daily-games'>
            <div className='daily-head'>
                <h3>Daily games</h3>
                <span className={`daily-status ${auto.enabled ? 'on' : 'off'}`}>
                    {auto.enabled ? 'On' : 'Off'}
                </span>
            </div>
            <p className='meta'>
                At each game time the league plays its next game day by itself: regular season and postseason.
                {league.type === 'pro' ? ' Pro leagues usually play two game days a day.' : ' College leagues usually play one game day a day.'}
                {perDay ? ` This league's preset is ${perDay} a day.` : ''}
                {' '}In the offseason it takes one step a day, at the last game time.
            </p>
            {auto.enabled && next && <p className='daily-next'>{offseason ? 'Next offseason step' : 'Next games'}: <b>{next}</b> (your time)</p>}
            {auto.lastRunAt && <p className='meta'>Last played automatically {midSentence(whenText(new Date(auto.lastRunAt)))}.</p>}
            {auto.lastError && <p className='daily-error'>The last automatic run failed: {auto.lastError.message}</p>}

            <div className='daily-form'>
                <fieldset className='daily-times'>
                    <legend>Game times ({form.timeZone.replace(/_/g, ' ')})</legend>
                    {form.times.map((t, i) => (
                        <div key={i} className='time-row'>
                            <input id={`game-time-${i}`} type='time' value={t} step='3600' aria-label={`Game time ${i + 1}`}
                                onChange={(e) => setTime(i, e.target.value)} />
                            {form.times.length > 1 &&
                                <button className='g-button small-btn' onClick={() => setForm({ ...form, times: form.times.filter((_, j) => j !== i) })}>Remove</button>
                            }
                        </div>
                    ))}
                    <p className='meta'>Games start at the top of the hour; a time like 8:30 plays at 9:00.</p>
                    {form.times.length < MAX_TIMES &&
                        <button className='g-button small-btn add-time' onClick={() => setForm({ ...form, times: [...form.times, '13:00'] })}>Add a game time</button>
                    }
                </fieldset>
                <div className='field'>
                    <label htmlFor='game-zone'>Time zone</label>
                    <select id='game-zone' value={form.timeZone} onChange={(e) => setForm({ ...form, timeZone: e.target.value })}>
                        {zones.map(z => <option key={z} value={z}>{z.replace(/_/g, ' ')}{z === ownZone ? ' (yours)' : ''}</option>)}
                    </select>
                </div>
            </div>

            {errors.length > 0 && <p className='daily-error'>{errors[0]}</p>}
            {message && <p className={message.error ? 'daily-error' : 'daily-ok'} role='status'>{message.text}</p>}

            <div className='daily-buttons'>
                {dirty &&
                    <button className={`g-button ${saving ? 'disabled' : ''}`} disabled={saving || errors.length > 0} onClick={() => save()}>
                        Save times
                    </button>
                }
                {auto.enabled ?
                    <button className={`g-button ${saving ? 'disabled' : ''}`} disabled={saving || errors.length > 0} onClick={() => save({ enabled: false })}>
                        {saving ? <ThreeDotsLoader /> : 'Turn off daily games'}
                    </button> :
                    <button className={`g-button primary ${saving ? 'disabled' : ''}`} disabled={saving || errors.length > 0} onClick={() => save({ enabled: true })}>
                        {saving ? <ThreeDotsLoader /> : 'Start daily games'}
                    </button>
                }
            </div>
        </div>
    )
}

export default DailyGames
